import { useFocus } from '@react-aria/interactions'
import cx from 'classnames'
import { forwardRef, useState } from 'react'
import { IconButton } from 'components/IconButton/IconButton'
import { useObjectRef } from '@react-aria/utils'

import Styles from './PromptInput.module.scss'
import { Send2 } from 'blaze-ui/icons/16'
import { AI } from 'blaze-ui/icons'

interface Props {
  prompt: string
  onChange: (prompt: string) => any
  placeholder?: string
  isDisabled?: boolean
  onSubmit: () => void
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  className?: string
  withIcon?: boolean
  withStartIcon?: boolean
  onFocusChange?: (focused: boolean) => void
  largeInput?: boolean
  classNameTextarea?: string
}

export const PromptInput = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      prompt,
      onChange,
      placeholder,
      isDisabled,
      onSubmit,
      onPaste,
      onKeyDown,
      className,
      withIcon = true,
      withStartIcon = false,
      onFocusChange,
      largeInput,
      classNameTextarea,
    },
    fRef,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const { focusProps } = useFocus({
      onFocusChange: (isFocused) => {
        setIsFocused(isFocused)
        onFocusChange?.(isFocused)
      },
    })
    const isValidInput = prompt.trim().length >= 1

    const textareaRef = useObjectRef<HTMLTextAreaElement>(fRef)

    const onResize = () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = ''
        const height = Math.min(100, textareaRef.current.scrollHeight)
        textareaRef.current.style.height = `${height}px`
      }
    }

    return (
      <div className={cx(className, Styles.prompt, { [Styles.focused]: isFocused, [Styles.largeInput]: largeInput })}>
        {withStartIcon && <AI />}
        <textarea
          className={cx(Styles.textarea, classNameTextarea)}
          ref={textareaRef}
          id="ask-blaze"
          data-testid="ask-blaze"
          onChange={(e) => {
            onChange(e.currentTarget.value)
            onResize()
          }}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
          value={prompt}
          name="ask-blaze"
          placeholder={placeholder || 'Tell Blaze what to do next...'}
          aria-label="Tell Blaze what to do"
          {...focusProps}
        />
        {withIcon && (
          <IconButton
            aria-label="Ask Blaze"
            icon={Send2}
            className={Styles.submitButton}
            variant={isValidInput ? 'primary' : 'tertiary'}
            isDisabled={!isValidInput || isDisabled}
            onPress={onSubmit}
            size={largeInput ? 'md' : 'sm'}
          />
        )}
      </div>
    )
  },
)
